<template>
  <div class="min-h-screen">
    <div class="max-w-7xl mx-auto p-4 sm:p-6 md:p-8">
      <transition name="view-fade" mode="out-in">
        <div v-if="currentView === 'list'" key="list">
          <header class="mb-8">
            <h1 class="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-slate-100 text-center sm:text-left">
              Assinaturas <span class="text-brand-600 dark:text-brand-500">Compartilhadas</span>
            </h1>
            <p class="text-sm text-slate-600 dark:text-slate-400 text-center sm:text-left mt-1">
              Divida os custos de assinaturas com amigos e família.
            </p>
          </header>
          <div class="mb-8 flex justify-start">
            <button @click="handleNovaAssinatura"
              class="btn-primary flex items-center justify-center text-sm shadow-lg hover:shadow-brand-700/50 transition-all">
              <span class="material-symbols-outlined mr-2 text-lg">group_add</span>
              Criar Grupo de Assinatura
            </button>
          </div>

          <div class="mb-8 p-4 bg-white dark:bg-brand-800 rounded-lg border border-slate-200 dark:border-slate-700">
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
              <div class="md:col-span-1">
                <label for="search" class="form-label text-sm">Buscar por nome</label>
                <div class="relative">
                  <span
                    class="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">search</span>
                  <input id="search" v-model.trim="searchQuery" type="text" placeholder="Ex: Netflix, Spotify..."
                    class="form-input !pl-10" />
                </div>
              </div>
              <div class="md:col-span-1">
                <label for="status" class="form-label text-sm">Filtrar por status</label>
                <select id="status" v-model="statusFilter" class="form-input">
                  <option>Todos</option>
                  <option>Pago</option>
                  <option>Atrasado</option>
                  <option>Aguardando Pagamento</option>
                </select>
              </div>
              <div class="md:col-span-1 flex items-center justify-start md:pb-1">
                <div class="flex items-center">
                  <input id="myTurn" v-model="myTurnFilter" type="checkbox"
                    class="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500">
                  <label for="myTurn" class="ml-2 block text-sm font-medium text-slate-700 dark:text-slate-300">Minha
                    vez de pagar</label>
                </div>
              </div>
            </div>
          </div>
          <div v-if="convitesPendentes.length > 0" class="mb-12">
            <h2
              class="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-4 border-b pb-2 border-slate-200 dark:border-slate-700">
              Convites Pendentes</h2>
            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              <div v-for="convite in convitesPendentes" :key="convite.conviteId"
                class="bg-white dark:bg-brand-800 p-5 rounded-2xl shadow-lg border-2 border-brand-500 animate-pulse-fast">
                <p class="text-sm text-slate-600 dark:text-slate-400">
                  <strong class="text-slate-800 dark:text-slate-200">{{ convite.nomeConvidadoPor }}</strong> convidou
                  você para:
                </p>
                <h4 class="text-lg font-bold text-brand-600 dark:text-brand-500 mt-1 truncate">{{ convite.nomeEntidade
                  }}</h4>
                <div class="flex items-center justify-end gap-3 mt-4">
                  <button @click="handleDecline(convite.conviteId)"
                    class="btn-danger-outline !text-xs !py-1.5 !px-3">Recusar</button>
                  <button @click="handleAccept(convite.conviteId)"
                    class="btn-primary !text-xs !py-1.5 !px-3">Aceitar</button>
                </div>
              </div>
            </div>
          </div>

          <div v-if="isLoading" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div v-for="i in 3" :key="i" class="skeleton-card">
              <div class="h-16 bg-slate-200 dark:bg-slate-700"></div>
              <div class="p-5 space-y-4">
                <div class="h-3 w-3/4 bg-slate-200 dark:bg-slate-700 rounded"></div>
                <div class="h-3 w-1/2 bg-slate-200 dark:bg-slate-700 rounded"></div>
              </div>
            </div>
          </div>
          <div v-else-if="assinaturas.length === 0 && convitesPendentes.length === 0" class="empty-state">
            <span class="material-symbols-outlined text-6xl text-brand-400 dark:text-brand-700">subscriptions</span>
            <p class="mt-4 font-medium">Você ainda não participa de nenhum grupo.</p>
            <p class="text-sm">Crie um novo grupo ou aceite um convite para começar.</p>
          </div>
          <transition-group v-else-if="filteredAssinaturas.length > 0" name="list" tag="div"
            class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div v-for="a in filteredAssinaturas" :key="a.uuid" @click="selectAssinatura(a.uuid)"
              class="card-assinatura group" :class="{ 'ring-2 ring-brand-500': isMyTurn(a) }">
              <a :href="getPlatformStyle(a).url" target="_blank" rel="noopener noreferrer" @click.stop
                class="p-4 flex items-center text-white" :class="getPlatformStyle(a).bg">
                <div class="w-10 h-10 rounded-md bg-white/25 flex items-center justify-center flex-shrink-0 p-1">
                  <img :src="getPlatformStyle(a).iconUrl" class="h-full w-full object-contain" :alt="a.nomeStreamer" />
                </div>
                <div class="ml-3 truncate">
                  <h3 class="font-bold text-lg truncate">{{ a.nomeStreamer }}</h3>
                  <p class="text-xs opacity-80 truncate">{{ a.plataforma }}</p>
                </div>
              </a>
              <div class="p-5 flex-grow space-y-3">
                <div class="flex justify-between items-center text-sm"><span
                    class="text-slate-500 dark:text-slate-400">Custo Mensal</span><span
                    class="font-bold text-slate-800 dark:text-slate-100">{{ displayCurrency(a.valorMensal) }}</span>
                </div>
                <div class="flex justify-between items-center text-sm"><span
                    class="text-slate-500 dark:text-slate-400">Sua Parte</span><span
                    class="font-bold text-slate-800 dark:text-slate-100">{{ displayCurrency(a.valorPorPessoa) }}</span>
                </div>
                <div class="flex justify-between items-center text-sm"><span
                    class="text-slate-500 dark:text-slate-400">Próximo a Pagar</span><span
                    class="font-semibold text-brand-600 dark:text-brand-400">{{ a.proximoPagador?.nome?.split(' ')[0] ??
                    'N/A' }}</span></div>
              </div>
              <div
                class="mt-auto p-3 bg-slate-50 dark:bg-brand-800/50 border-t border-slate-200/50 dark:border-slate-700/50 flex justify-between items-center">
                <div class="flex items-center -space-x-2">
                  <div v-for="membro in (a.membros || []).slice(0, 4)" :key="membro.id"
                    class="w-7 h-7 rounded-full bg-brand-100 dark:bg-brand-900 text-brand-700 dark:text-brand-300 flex items-center justify-center text-xs font-bold ring-2 ring-white dark:ring-brand-800"
                    :title="membro.nome ?? 'Nome não disponível'">{{ membro.nome?.charAt(0).toUpperCase() ?? '?' }}
                  </div>
                  <div v-if="(a.membros?.length ?? 0) > 4"
                    class="w-7 h-7 rounded-full bg-slate-200 dark:bg-slate-600 text-slate-600 dark:text-slate-300 flex items-center justify-center text-xs font-bold ring-2 ring-white dark:ring-brand-800">
                    +{{ a.membros.length - 4 }}</div>
                </div>
                <span class="px-2 py-0.5 text-xs font-semibold rounded-full"
                  :class="statusClass(a.statusPagamentoAtual)">{{ a.statusPagamentoAtual }}</span>
              </div>
            </div>
          </transition-group>
          <div v-else-if="assinaturas.length > 0 && filteredAssinaturas.length === 0" class="empty-state">
            <span class="material-symbols-outlined text-6xl text-brand-400 dark:text-brand-700">search_off</span>
            <p class="mt-4 font-medium">Nenhum resultado encontrado.</p>
            <p class="text-sm">Tente ajustar seus filtros de busca.</p>
          </div>
        </div>

        <div v-else-if="currentView === 'details' && selectedAssinatura" key="details">
          <button @click="returnToList"
            class="mb-4 flex items-center text-sm font-semibold text-brand-600 hover:text-brand-700 dark:text-brand-500 dark:hover:text-brand-500/80 transition-colors">
            <span class="material-symbols-outlined mr-1">arrow_back</span>Voltar
          </button>

          <header class="bg-white dark:bg-brand-800 p-6 rounded-2xl shadow-lg mb-8">
            <div class="md:flex justify-between items-start">
              <div class="flex items-center space-x-4 mb-4 md:mb-0">
                <img :src="getPlatformStyle(selectedAssinatura).iconUrl" :alt="selectedAssinatura.nomeStreamer"
                  class="w-16 h-16 rounded-xl bg-slate-100 dark:bg-slate-700 p-2 object-contain flex-shrink-0" />
                <div>
                  <h1 class="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-slate-100">{{
                    selectedAssinatura.nomeStreamer }}</h1>
                  <p class="text-sm text-slate-600 dark:text-slate-400 mt-1">{{ selectedAssinatura.plataforma }}</p>
                </div>
              </div>
              <span class="px-3 py-1 text-sm font-semibold rounded-full flex-shrink-0 self-start md:self-center"
                :class="statusClass(selectedAssinatura.statusPagamentoAtual)">
                {{ selectedAssinatura.statusPagamentoAtual }}
              </span>
            </div>
            <div
              class="mt-6 border-t border-slate-200 dark:border-slate-700 pt-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 text-center gap-6">
              <div>
                <p class="text-sm text-slate-500 dark:text-slate-400">Valor Mensal</p>
                <p class="font-bold text-2xl text-slate-800 dark:text-white">{{
                  displayCurrency(selectedAssinatura.valorMensal) }}</p>
              </div>
              <div>
                <p class="text-sm text-slate-500 dark:text-slate-400">Valor por Pessoa</p>
                <p class="font-bold text-2xl text-slate-800 dark:text-white">{{
                  displayCurrency(selectedAssinatura.valorPorPessoa) }}</p>
              </div>
              <div class="sm:col-span-2 md:col-span-1">
                <p class="text-sm text-slate-500 dark:text-slate-400">Próximo a Pagar</p>
                <p class="font-bold text-2xl text-brand-600 dark:text-brand-400">{{
                  selectedAssinatura.proximoPagador?.nome?.split(' ')[0] ?? 'N/A' }}</p>
              </div>
            </div>
            <div class="mt-6 border-t border-slate-200 dark:border-slate-700 pt-6">
              <h3 class="text-lg font-bold text-slate-800 dark:text-slate-100 mb-4">Ações</h3>
              <div class="grid grid-cols-1 sm:grid-cols-2 lg:flex lg:flex-wrap gap-3">
                <button v-if="canPayNow" @click="openAddPagamentoModal"
                  class="btn-primary flex items-center justify-center text-sm"
                  :class="{ 'animate-pulse': isMyTurn(selectedAssinatura) }">
                  <span class="material-symbols-outlined mr-2 text-lg">savings</span>
                  Registrar Pagamento
                </button>
                <button v-if="isUserTheCreator" @click="showInviteModal = true"
                  class="btn-secondary flex items-center justify-center text-sm">
                  <span class="material-symbols-outlined mr-2 text-lg">person_add</span>
                  Convidar Membro
                </button>
                <button v-if="isUserTheCreator" @click="handleEditAssinatura"
                  class="btn-secondary flex items-center justify-center text-sm">
                  <span class="material-symbols-outlined mr-2 text-lg">edit</span>
                  Editar Assinatura
                </button>
              </div>
            </div>
          </header>

          <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <h2 class="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-6">Histórico de Pagamentos</h2>
              <div v-if="(selectedAssinatura.pagamentos?.length ?? 0) === 0"
                class="empty-state flex-col justify-center">
                <span class="material-symbols-outlined text-6xl text-brand-400 dark:text-brand-700">receipt_long</span>
                <p class="mt-4 font-medium text-slate-700 dark:text-slate-300">Nenhum pagamento registrado.</p>
              </div>
              <div v-else class="bg-white dark:bg-brand-800 rounded-2xl shadow-lg max-h-[600px] overflow-y-auto p-6">
                <div class="relative timeline-container">
                  <div v-for="p in selectedAssinatura.pagamentos" :key="p.id" class="timeline-item group">
                    <div class="timeline-point"></div>
                    <div class="timeline-content">
                      <div class="flex items-center justify-between">
                        <div>
                          <p class="font-semibold text-slate-800 dark:text-slate-100">{{ displayCurrency(p.valor) }}</p>
                          <p class="text-sm text-slate-500 dark:text-slate-400">por {{ p.usuarioNome }}</p>
                          <p class="text-xs text-slate-400 dark:text-slate-500">{{ formatDateTime(p.dataPagamento) }}
                          </p>
                        </div>
                        <div class="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button v-if="isUserTheCreator" @click="openEditPagamentoModal(p)"
                            class="btn-action-icon text-sky-500 dark:text-sky-400 hover:bg-slate-100 dark:hover:bg-slate-700"
                            title="Editar pagamento">
                            <span class="material-symbols-outlined text-lg">edit</span>
                          </button>
                          <button v-if="isUserTheCreator" @click="confirmDeletePagamento(p)"
                            class="btn-action-icon text-red-500 dark:text-red-400 hover:bg-slate-100 dark:hover:bg-slate-700"
                            title="Remover pagamento">
                            <span class="material-symbols-outlined text-lg">delete</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <h2 class="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-6">Membros ({{
                selectedAssinatura.membros?.length ?? 0 }})</h2>
              <div v-if="(selectedAssinatura.membros?.length ?? 0) === 0"
                class="empty-state h-full flex-col justify-center">
                <span class="material-symbols-outlined text-6xl text-brand-400 dark:text-brand-700">group_off</span>
                <p class="mt-4 font-medium text-slate-700 dark:text-slate-300">Nenhum membro encontrado.</p>
              </div>
              <div v-else class="bg-white dark:bg-brand-800 rounded-2xl shadow-lg max-h-[600px] overflow-y-auto">
                <ul class="divide-y divide-slate-200 dark:divide-slate-700">
                  <li v-for="membro in sortedMembers" :key="membro.id"
                    class="p-4 flex items-center justify-between group transition-opacity"
                    :class="{ 'opacity-60': membro.status === 'PENDENTE' }">
                    <div class="flex items-center">
                      <div
                        class="w-10 h-10 rounded-full bg-brand-100 dark:bg-brand-900 text-brand-700 dark:text-brand-300 flex items-center justify-center text-sm font-bold ring-2 ring-white dark:ring-brand-800 flex-shrink-0">
                        {{ membro.nome?.charAt(0).toUpperCase() ?? '?' }}
                      </div>
                      <div class="ml-4">
                        <p class="font-semibold text-slate-800 dark:text-slate-100 flex items-center flex-wrap gap-2">
                          <span>{{ membro.nome }}</span>
                          <span v-if="membro.id === selectedAssinatura.criador?.id" class="tag-owner">Dono</span>
                          <span v-if="membro.status === 'PENDENTE'" class="tag-pending">Pendente</span>
                          <span
                            v-if="membro.id === selectedAssinatura.proximoPagador?.id && selectedAssinatura.statusPagamentoAtual !== 'Pago'"
                            class="tag-attention">Próximo a Pagar</span>
                        </p>
                        <p class="text-sm text-slate-500 dark:text-slate-400">{{ membro.email }}</p>
                      </div>
                    </div>
                    <div class="flex items-center">
                      <button v-if="isUserTheCreator && membro.id !== userId && membro.status === 'ATIVO'"
                        @click="confirmRemoveMember(membro)"
                        class="btn-danger-outline !p-2 !rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Remover membro">
                        <span class="material-symbols-outlined text-lg">person_remove</span>
                      </button>
                      <button v-if="isUserTheCreator && membro.id !== userId && membro.status === 'PENDENTE'"
                        @click="confirmCancelInvite(membro)"
                        class="btn-secondary !p-2 !rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Cancelar convite">
                        <span class="material-symbols-outlined text-lg">cancel</span>
                      </button>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <br /><br /><br />
          <div class="lg:col-span-2 mt-4">
            <h2 class="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-6">Zona de Perigo</h2>
            <div
              class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-500/30 p-5 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div v-if="isUserTheCreator">
                <h4 class="font-bold text-red-800 dark:text-red-300">Encerrar Grupo</h4>
                <p class="text-sm text-red-600 dark:text-red-300/80">Esta ação é irreversível e removerá todos os
                  membros.</p>
              </div>
              <button v-if="isUserTheCreator" @click="confirmEndGroup"
                class="btn-danger-outline flex items-center text-sm flex-shrink-0">
                <span class="material-symbols-outlined mr-2 text-lg">cancel</span>
                Encerrar Grupo
              </button>
              <div v-if="!isUserTheCreator">
                <h4 class="font-bold text-red-800 dark:text-red-300">Sair do Grupo</h4>
                <p class="text-sm text-red-600 dark:text-red-300/80">Você perderá o acesso a esta assinatura
                  compartilhada.</p>
              </div>
              <button v-if="!isUserTheCreator" @click="confirmLeaveGroup"
                class="btn-danger-outline flex items-center text-sm flex-shrink-0">
                <span class="material-symbols-outlined mr-2 text-lg">logout</span>
                Sair do Grupo
              </button>
            </div>
          </div>
        </div>
      </transition>
    </div>

    <transition name="modal-fade">
      <div v-if="showCreateModal" @click.self="showCreateModal = false" class="modal-backdrop">
        <div class="modal-container">
          <header class="modal-header">
            <h3>Criar Grupo de Assinatura</h3><button @click="showCreateModal = false" class="modal-close-btn"><span
                class="material-symbols-outlined">close</span></button>
          </header>
          <form @submit.prevent="handleCreate" class="p-6 space-y-4">
            <div>
              <label for="pNome" class="form-label">Nome do Serviço</label>
              <select id="pNome" v-model="createForm.nomeStreamer" @change="handleServiceSelection(createForm)" required
                class="form-input">
                <option disabled value="">Selecione um serviço...</option>
                <option v-for="service in availableServices" :key="service.key" :value="service.displayName">
                  {{ service.displayName }}
                </option>
              </select>
            </div>
            <div>
              <label for="pPlataforma" class="form-label">Plataforma</label>
              <input id="pPlataforma" v-model="createForm.plataforma" type="text" required
                placeholder="Selecione um serviço para preencher" class="form-input bg-slate-50 dark:bg-slate-700/50"
                readonly />
            </div>
            <div><label for="pValorMensal" class="form-label">Valor Mensal (R$)</label><input id="pValorMensal"
                v-model.number="createForm.valorMensal" type="number" step="0.01" required placeholder="29.90"
                class="form-input" /></div>
            <div><label for="pDiaVencimento" class="form-label">Dia do Vencimento</label><input id="pDiaVencimento"
                v-model.number="createForm.diaVencimento" type="number" required min="1" max="31" placeholder="15"
                class="form-input" /></div>
            <div><label for="pEmails" class="form-label">Convidar Membros (opcional)</label><textarea id="pEmails"
                v-model="createForm.emailsMembros" rows="3" placeholder="Separe os emails por vírgula ou espaço"
                class="form-input"></textarea>
            </div>
            <footer class="modal-footer"><button @click="showCreateModal = false" type="button"
                class="btn-secondary">Cancelar</button><button type="submit" :disabled="isSubmitting"
                class="btn-primary flex items-center"><span v-if="isSubmitting"
                  class="animate-spin material-symbols-outlined mr-2">progress_activity</span>{{
                    isSubmitting ? 'Criando...' : 'Criar Grupo' }}</button></footer>
          </form>
        </div>
      </div>
    </transition>

    <transition name="modal-fade">
      <div v-if="showEditModal" @click.self="showEditModal = false" class="modal-backdrop">
        <div class="modal-container">
          <header class="modal-header">
            <h3>Editar Grupo de Assinatura</h3><button @click="showEditModal = false" class="modal-close-btn"><span
                class="material-symbols-outlined">close</span></button>
          </header>
          <form @submit.prevent="handleUpdate" class="p-6 space-y-4">
            <div>
              <label for="pEditNome" class="form-label">Nome do Serviço</label>
              <select id="pEditNome" v-model="editForm.nomeStreamer" @change="handleServiceSelection(editForm)" required
                class="form-input">
                <option disabled value="">Selecione um serviço...</option>
                <option v-for="service in availableServices" :key="service.key" :value="service.displayName">
                  {{ service.displayName }}
                </option>
              </select>
            </div>
            <div>
              <label for="pEditPlataforma" class="form-label">Plataforma</label>
              <input id="pEditPlataforma" v-model="editForm.plataforma" type="text" required
                class="form-input bg-slate-50 dark:bg-slate-700/50" readonly />
            </div>
            <div><label for="pEditValorMensal" class="form-label">Valor Mensal (R$)</label><input id="pEditValorMensal"
                v-model.number="editForm.valorMensal" type="number" step="0.01" required class="form-input" /></div>
            <div><label for="pEditDiaVencimento" class="form-label">Dia do Vencimento</label><input
                id="pEditDiaVencimento" v-model.number="editForm.diaVencimento" type="number" required min="1" max="31"
                class="form-input" /></div>
            <footer class="modal-footer"><button @click="showEditModal = false" type="button"
                class="btn-secondary">Cancelar</button><button type="submit" :disabled="isSubmitting"
                class="btn-primary flex items-center"><span v-if="isSubmitting"
                  class="animate-spin material-symbols-outlined mr-2">progress_activity</span>{{
                    isSubmitting ? 'Salvando...' : 'Salvar Alterações' }}</button></footer>
          </form>
        </div>
      </div>
    </transition>

    <transition name="modal-fade">
      <div v-if="showInviteModal" @click.self="showInviteModal = false" class="modal-backdrop">
        <div class="modal-container">
          <header class="modal-header">
            <h3>Convidar Membro</h3><button @click="showInviteModal = false" class="modal-close-btn"><span
                class="material-symbols-outlined">close</span></button>
          </header>
          <form @submit.prevent="handleInvite" class="p-6 space-y-4">
            <div><label for="inviteEmail" class="form-label">Email do Convidado</label><input id="inviteEmail"
                v-model="inviteForm.email" type="email" required placeholder="nome@exemplo.com" class="form-input" />
            </div>
            <footer class="modal-footer"><button @click="showInviteModal = false" type="button"
                class="btn-secondary">Cancelar</button><button type="submit" :disabled="isSubmitting"
                class="btn-primary flex items-center"><span v-if="isSubmitting"
                  class="animate-spin material-symbols-outlined mr-2">progress_activity</span>{{ isSubmitting ?
                    'Convidando...' : 'Enviar Convite' }}</button></footer>
          </form>
        </div>
      </div>
    </transition>
    <transition name="modal-fade">
      <div v-if="showAddPagamentoModal" @click.self="showAddPagamentoModal = false" class="modal-backdrop">
        <div class="modal-container">
          <header class="modal-header">
            <h3>Registrar Pagamento</h3><button @click="showAddPagamentoModal = false" class="modal-close-btn"><span
                class="material-symbols-outlined">close</span></button>
          </header>
          <form @submit.prevent="handleAddPagamento" class="p-6 space-y-4">
            <div><label for="pValor" class="form-label">Valor do Pagamento (R$)</label><input id="pValor"
                v-model.number="pagamentoForm.valor" type="number" step="0.01" required class="form-input" /></div>
            <footer class="modal-footer"><button @click="showAddPagamentoModal = false" type="button"
                class="btn-secondary">Cancelar</button><button type="submit" :disabled="isSubmitting"
                class="btn-primary flex items-center"><span v-if="isSubmitting"
                  class="animate-spin material-symbols-outlined mr-2">progress_activity</span>{{ isSubmitting ?
                    'Registrando...' : 'Registrar' }}</button></footer>
          </form>
        </div>
      </div>
    </transition>
    <transition name="modal-fade">
      <div v-if="showEditPagamentoModal" @click.self="showEditPagamentoModal = false" class="modal-backdrop">
        <div class="modal-container">
          <header class="modal-header">
            <h3>Editar Pagamento</h3><button @click="showEditPagamentoModal = false" class="modal-close-btn"><span
                class="material-symbols-outlined">close</span></button>
          </header>
          <form @submit.prevent="handleUpdatePagamento" class="p-6 space-y-4">
            <div><label for="pEditValor" class="form-label">Novo Valor (R$)</label><input id="pEditValor"
                v-model.number="editPagamentoForm.valor" type="number" step="0.01" required class="form-input" /></div>
            <footer class="modal-footer"><button @click="showEditPagamentoModal = false" type="button"
                class="btn-secondary">Cancelar</button><button type="submit" :disabled="isSubmitting"
                class="btn-primary flex items-center"><span v-if="isSubmitting"
                  class="animate-spin material-symbols-outlined mr-2">progress_activity</span>{{ isSubmitting ?
                    'Salvando...' : 'Salvar' }}</button></footer>
          </form>
        </div>
      </div>
    </transition>
    <transition name="modal-fade">
      <div v-if="showConfirmEndGroup" @click.self="showConfirmEndGroup = false" class="modal-backdrop">
        <div class="modal-container max-w-md p-0">
          <div class="p-6 text-center"><span
              class="material-symbols-outlined text-5xl text-red-500 dark:text-red-400 mx-auto">cancel</span>
            <h3 class="modal-title">Encerrar Grupo?</h3>
            <p class="modal-text">Tem certeza que deseja encerrar o grupo <strong>{{ selectedAssinatura?.nomeStreamer
                }}</strong>? Esta ação é irreversível.</p>
          </div>
          <div class="modal-footer"><button @click="showConfirmEndGroup = false" type="button"
              class="btn-secondary">Cancelar</button><button @click="executeEndGroup" class="btn-danger">Sim,
              Encerrar</button></div>
        </div>
      </div>
    </transition>
    <transition name="modal-fade">
      <div v-if="showConfirmRemoveMember" @click.self="showConfirmRemoveMember = false" class="modal-backdrop">
        <div class="modal-container max-w-md p-0">
          <div class="p-6 text-center"><span
              class="material-symbols-outlined text-5xl text-amber-500 dark:text-amber-400 mx-auto">person_remove</span>
            <h3 class="modal-title">Remover Membro?</h3>
            <p class="modal-text">Tem certeza que deseja remover <strong>{{ memberToRemove?.nome }}</strong> do grupo?
            </p>
          </div>
          <div class="modal-footer"><button @click="showConfirmRemoveMember = false" type="button"
              class="btn-secondary">Cancelar</button><button @click="executeRemoveMember" class="btn-danger">Sim,
              Remover</button></div>
        </div>
      </div>
    </transition>
    <transition name="modal-fade">
      <div v-if="showConfirmCancelInvite" @click.self="showConfirmCancelInvite = false" class="modal-backdrop">
        <div class="modal-container max-w-md p-0">
          <div class="p-6 text-center"><span
              class="material-symbols-outlined text-5xl text-amber-500 dark:text-amber-400 mx-auto">cancel</span>
            <h3 class="modal-title">Cancelar Convite?</h3>
            <p class="modal-text">Tem certeza que deseja cancelar o convite enviado para <strong>{{ inviteToCancel?.nome
                }}</strong>?</p>
          </div>
          <div class="modal-footer"><button @click="showConfirmCancelInvite = false" type="button"
              class="btn-secondary">Voltar</button><button @click="executeCancelInvite" class="btn-danger">Sim,
              Cancelar</button></div>
        </div>
      </div>
    </transition>
    <transition name="modal-fade">
      <div v-if="showConfirmLeaveGroup" @click.self="showConfirmLeaveGroup = false" class="modal-backdrop">
        <div class="modal-container max-w-md p-0">
          <div class="p-6 text-center"><span
              class="material-symbols-outlined text-5xl text-amber-500 dark:text-amber-400 mx-auto">logout</span>
            <h3 class="modal-title">Sair do Grupo?</h3>
            <p class="modal-text">Tem certeza que deseja sair do grupo <strong>{{ selectedAssinatura?.nomeStreamer
                }}</strong>?</p>
          </div>
          <div class="modal-footer"><button @click="showConfirmLeaveGroup = false" type="button"
              class="btn-secondary">Cancelar</button><button @click="executeLeaveGroup" class="btn-danger">Sim,
              Sair</button></div>
        </div>
      </div>
    </transition>
    <transition name="modal-fade">
      <div v-if="showConfirmDeletePagamento" @click.self="showConfirmDeletePagamento = false" class="modal-backdrop">
        <div class="modal-container max-w-md p-0">
          <div class="p-6 text-center"><span
              class="material-symbols-outlined text-5xl text-red-500 dark:text-red-400 mx-auto">warning</span>
            <h3 class="modal-title">Excluir Pagamento?</h3>
            <p class="modal-text">Tem certeza que deseja excluir este pagamento de <strong>{{
              displayCurrency(pagamentoToDelete?.valor) }}</strong>?</p>
          </div>
          <div class="modal-footer"><button @click="showConfirmDeletePagamento = false" type="button"
              class="btn-secondary">Cancelar</button><button @click="executeDeletePagamento" class="btn-danger">Sim,
              Excluir</button></div>
        </div>
      </div>
    </transition>

    <transition name="notification">
      <div v-if="notification.show" class="hidden md:block fixed top-5 right-5 w-full max-w-sm z-[70]">
        <div class="p-4 rounded-lg shadow-2xl flex items-start text-white"
          :class="notification.type === 'success' ? 'bg-green-500' : 'bg-red-500'">
          <div class="flex-shrink-0"><span class="material-symbols-outlined">{{ notification.type === 'success' ?
            'check_circle' : 'error' }}</span></div>
          <div class="ml-3 flex-1">
            <p class="text-sm font-bold">{{ notification.message }}</p>
          </div>
          <div class="ml-auto pl-3"><button @click="notification.show = false"
              class="-mx-1.5 -my-1.5 p-1.5 rounded-md hover:bg-black/20 focus:outline-none focus:ring-2 focus:ring-white"><span
                class="material-symbols-outlined text-lg">close</span></button></div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import axios from 'axios';

const route = useRoute();
const router = useRouter();

const assinaturas = ref([]);
const convitesPendentes = ref([]);
const selectedAssinatura = ref(null);
const userId = ref(null);
const currentView = ref('list');
const isLoading = ref(true);
const isSubmitting = ref(false);

const isMobile = ref(false);
const checkDevice = () => {
  isMobile.value = window.innerWidth < 768; 
};

const showCreateModal = ref(false);
const showEditModal = ref(false);
const showInviteModal = ref(false);
const showAddPagamentoModal = ref(false);
const showEditPagamentoModal = ref(false);
const showConfirmEndGroup = ref(false);
const showConfirmRemoveMember = ref(false);
const showConfirmLeaveGroup = ref(false);
const showConfirmDeletePagamento = ref(false);
const showConfirmCancelInvite = ref(false);

const memberToRemove = ref(null);
const pagamentoToDelete = ref(null);
const pagamentoToEdit = ref(null);
const inviteToCancel = ref(null);

const createForm = ref({ nomeStreamer: '', plataforma: '', valorMensal: null, diaVencimento: null, emailsMembros: '' });
const editForm = ref({ nomeStreamer: '', plataforma: '', valorMensal: null, diaVencimento: null });
const inviteForm = ref({ email: '' });
const pagamentoForm = ref({ valor: null });
const editPagamentoForm = ref({ valor: null });

const searchQuery = ref('');
const statusFilter = ref('Todos');
const myTurnFilter = ref(false);

const notification = ref({ show: false, type: 'success', message: '' });
const notificationTimer = ref(null);

const API_BASE_URL = 'https://backend.controlease.com.br/api';
const authConfig = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const handleNovaAssinatura = () => {
  if (isMobile.value) {
    router.push({ name: 'AssinaturaCompartilhadaAdd' });
  } else {
    createForm.value = { nomeStreamer: '', plataforma: '', valorMensal: null, diaVencimento: null, emailsMembros: '' }; // Reset form
    showCreateModal.value = true;
  }
};

const handleEditAssinatura = () => {
  if (!selectedAssinatura.value) return;
  if (isMobile.value) {
    router.push({ name: 'AssinaturaCompartilhadaEdit', params: { uuid: selectedAssinatura.value.uuid } });
  } else {
    openEditModal(); 
  }
};

const displayCurrency = (value) => value?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) ?? 'R$ 0,00';
const formatDateTime = (dateTimeString) => {
  if (!dateTimeString) return '';
  return new Date(dateTimeString).toLocaleString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
};

const isUserTheCreator = computed(() => selectedAssinatura.value?.criador?.id === userId.value);
const canPayNow = computed(() => selectedAssinatura.value?.statusPagamentoAtual !== 'Pago');
const isMyTurn = (assinatura) => {
  return assinatura.proximoPagador?.id === userId.value && assinatura.statusPagamentoAtual !== 'Pago';
};
const sortedMembers = computed(() => {
  return selectedAssinatura.value?.membros ? [...selectedAssinatura.value.membros].sort((a, b) => (a.nome ?? '').localeCompare(b.nome ?? '')) : [];
});

const filteredAssinaturas = computed(() => {
  let filtered = assinaturas.value;
  if (searchQuery.value) {
    filtered = filtered.filter(a => a.nomeStreamer.toLowerCase().includes(searchQuery.value.toLowerCase()));
  }
  if (statusFilter.value !== 'Todos') {
    filtered = filtered.filter(a => a.statusPagamentoAtual === statusFilter.value);
  }
  if (myTurnFilter.value) {
    filtered = filtered.filter(a => isMyTurn(a));
  }
  return filtered;
});

const showNotification = (type, message, duration = 4000) => {
  if (notificationTimer.value) clearTimeout(notificationTimer.value);
  notification.value = { show: true, type, message };
  notificationTimer.value = setTimeout(() => { notification.value.show = false; }, duration);
}

const statusClass = (status) => {
  switch (status) {
    case 'Pago': return 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300';
    case 'Atrasado': return 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300';
    case 'Aguardando Pagamento': return 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300';
    default: return 'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-300';
  }
};

const platformStyles = {
    'default': { displayName: 'Outro', plataforma: 'Serviço', bg: 'bg-slate-500', iconUrl: 'https://cdn-icons-png.flaticon.com/512/681/681458.png', url: '#', color: { light: '#64748B', dark: '#94A3B8' } },
    'netflix': { displayName: 'Netflix', plataforma: 'Streaming de Vídeo', bg: 'bg-[#E50914]', iconUrl: 'https://images.ctfassets.net/4cd45et68cgf/Rx83JoRDMkYNlMC9MKzcB/2b14d5a59fc3937afd3f03191e19502d/Netflix-Symbol.png?w=700&h=456', url: 'https://www.netflix.com' },
    'spotify': { displayName: 'Spotify', plataforma: 'Streaming de Música', bg: 'bg-[#1DB954]', iconUrl: 'https://www.freepnglogos.com/uploads/spotify-logo-png/spotify-icon-marilyn-scott-0.png', url: 'https://www.spotify.com' },
    'twitch': { displayName: 'Twitch', plataforma: 'Streaming de Vídeo', bg: 'bg-[#9146FF]', iconUrl: 'https://img.icons8.com/ios-filled/500/ffffff/twitch.png', url: 'https://www.twitch.tv' },
    'youtube': { displayName: 'YouTube', plataforma: 'Streaming de Vídeo', bg: 'bg-[#FF0000]', iconUrl: 'https://logodownload.org/wp-content/uploads/2014/10/youtube-logo-5-2.png', url: 'https://www.youtube.com' },
    'prime video': { displayName: 'Prime Video', plataforma: 'Streaming de Vídeo', bg: 'bg-[#00A8E1]', iconUrl: 'https://m.media-amazon.com/images/I/31W9hs7w0JL.png', url: 'https://www.primevideo.com' },
    'disney+': { displayName: 'Disney+', plataforma: 'Streaming de Vídeo', bg: 'bg-[#111D4A]', iconUrl: 'https://logodownload.org/wp-content/uploads/2020/11/disney-plus-logo-1.png', url: 'https://www.disneyplus.com' },
    'max': { displayName: 'Max', plataforma: 'Streaming de Vídeo', bg: 'bg-gradient-to-tr from-blue-600 to-purple-600', iconUrl: 'https://images-wixmp-ed30a86b8c4ca887773594c2.wixmp.com/f/5fbef0ad-821e-42ff-bd50-c54a0742abc7/dfudrq1-f161a753-d067-4c18-84f6-ca87785a41fe.png?token=eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJzdWIiOiJ1cm46YXBwOjdlMGQxODg5ODIyNjQzNzNhNWYwZDQxNWVhMGQyNmUwIiwiaXNzIjoidXJuOmFwcDo3ZTBkMTg4OTgyMjY0MzczYTVmMGQ0MTVlYTBkMjZlMCIsIm9iaiI6W1t7InBhdGgiOiJcL2ZcLzVmYmVmMGFkLTgyMWUtNDJmZi1iZDUwLWM1NGEwNzQyYWJjN1wvZGZ1ZHJxMS1mMTYxYTc1My1kMDY3LTRjMTgtODRmNi1jYTg3Nzg1YTQxZmUucG5nIn1dXSwiYXVkIjpbInVybjpzZXJ2aWNlOmZpbGUuZG93bmxvYWQiXX0.6vm5FkCJYrsG9Qh3xfbV0LMEi43AbT5cqqJinwJSLZM', url: 'https://www.max.com' },
    'crunchyroll': { displayName: 'Crunchyroll', plataforma: 'Streaming de Vídeo', bg: 'bg-[#F47521]', iconUrl: 'https://img.icons8.com/ios-filled/500/ffffff/crunchyroll.png', url: 'https://www.crunchyroll.com' },
    'paramount+': { displayName: 'Paramount+', plataforma: 'Streaming de Vídeo', bg: 'bg-[#0064FF]', iconUrl: 'https://wallpapers.com/images/hd/paramount-picture-rapar3xy1d9cfv3q.jpg', url: 'https://www.paramountplus.com' },
    'apple tv+': { displayName: 'Apple TV+', plataforma: 'Streaming de Vídeo', bg: 'bg-gray-800', iconUrl: 'https://hyken.com/wp-content/uploads/2023/01/Apple-TV-Logo-2019-present.png', url: 'https://tv.apple.com' },
    'globoplay': { displayName: 'Globoplay', plataforma: 'Streaming de Vídeo', bg: 'bg-gradient-to-tr from-pink-500 to-red-600', iconUrl: 'https://m.media-amazon.com/images/I/71bch7gUsqL.png', url: 'https://globoplay.globo.com' },
    'chatgpt': { displayName: 'ChatGPT', plataforma: 'Produtividade', bg: 'bg-[#74A99C]', iconUrl: 'https://upload.wikimedia.org/wikipedia/commons/0/04/ChatGPT_logo.svg', url: 'https://chat.openai.com' },
    'google one': { displayName: 'Google One', plataforma: 'Armazenamento', bg: 'bg-gradient-to-r from-blue-500 via-red-500 to-yellow-500', iconUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/c/c1/Google_%22G%22_logo.svg/768px-Google_%22G%22_logo.svg.png', url: 'https://one.google.com' },
    'microsoft 365': { displayName: 'Microsoft 365', plataforma: 'Produtividade', bg: 'bg-[#8A2BE2]', iconUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/0/0e/Microsoft_365_%282022%29.svg/931px-Microsoft_365_%282022%29.svg.png', url: 'https://www.microsoft.com/microsoft-365' },
    'adobe': { displayName: 'Adobe Creative Cloud', plataforma: 'Produtividade', bg: 'bg-red-500', iconUrl: 'https://img.icons8.com/ios-filled/500/ffffff/adobe-logo.png', url: 'https://www.adobe.com/creativecloud.html' },
    'canva': { displayName: 'Canva Pro', plataforma: 'Design', bg: 'bg-gradient-to-r from-purple-500 to-blue-500', iconUrl: 'https://www.luiconceitocriativo.com/wp-content/uploads/2024/08/244421955_1062499494497845_7683090676434840618_n-1024x1024.jpg', url: 'https://www.canva.com' },
    'r7': { displayName: 'R7 Play', plataforma: 'Streaming de Vídeo', bg: 'bg-indigo-700', iconUrl: 'https://play-lh.googleusercontent.com/EuDnuqP6BMGuHHu6B8uh9T24_fKlTRZwjJHWyS_HIqGXYc7I5zuCQMswbaHCVNYPlos', url: 'https://play.r7.com' },
    'deezer': { displayName: 'Deezer', plataforma: 'Streaming de Música', bg: 'bg-purple-600', iconUrl: 'https://e-cdns-images.dzcdn.net/images/misc/21af5821f36402283e33f37f3729e2f6/192x192.jpg', url: 'https://www.deezer.com' }
};

const availableServices = computed(() => Object.keys(platformStyles).map(key => ({ key, ...platformStyles[key] })).sort((a, b) => a.displayName.localeCompare(b.displayName)));

const handleServiceSelection = (formRef) => {
  const serviceData = availableServices.value.find(s => s.displayName === formRef.nomeStreamer);
  if (serviceData) {
    formRef.plataforma = serviceData.plataforma;
  }
};

const getPlatformStyle = (assinatura) => {
  const name = (assinatura?.nomeStreamer || '').toLowerCase();
  for (const key in platformStyles) {
    if (name.includes(platformStyles[key].displayName.toLowerCase())) {
      return platformStyles[key];
    }
  }
  return platformStyles.default;
};

const fetchCurrentUser = () => {
  const localUserId = localStorage.getItem('usuario_id');
  if (localUserId) {
    userId.value = parseInt(localUserId, 10);
  }
};

const fetchAssinaturas = async () => {
  isLoading.value = true;
  try {
    const response = await axios.get(`${API_BASE_URL}/assinaturas-compartilhadas`, authConfig());
    assinaturas.value = response.data.assinaturasAtivas || [];
    convitesPendentes.value = response.data.convitesPendentes || [];
  } catch (error) {
    showNotification('error', 'Erro ao buscar assinaturas.');
  } finally {
    isLoading.value = false;
  }
};

const selectAssinatura = async (uuid) => {
  isLoading.value = true;
  try {
    const response = await axios.get(`${API_BASE_URL}/assinaturas-compartilhadas/${uuid}`, authConfig());
    selectedAssinatura.value = response.data;
    currentView.value = 'details';
    if (route.params.id !== uuid) {
      router.push({ name: 'AssinaturasCompartilhadasUser', params: { id: uuid } });
    }
  } catch (error) {
    showNotification('error', 'Erro ao buscar detalhes da assinatura.');
    router.push({ name: 'AssinaturasCompartilhadasUser' }); 
  } finally {
    isLoading.value = false;
  }
};

const returnToList = async () => {
  currentView.value = 'list';
  selectedAssinatura.value = null;
  router.push({ name: 'AssinaturasCompartilhadasUser', params: { id: undefined } });
  await fetchAssinaturas(); 
};


const refreshSelectedAssinatura = async () => {
  if (!selectedAssinatura.value?.uuid) return;
  try {
    const response = await axios.get(`${API_BASE_URL}/assinaturas-compartilhadas/${selectedAssinatura.value.uuid}`, authConfig());
    selectedAssinatura.value = response.data;
  } catch (error) {
    showNotification('error', 'Erro ao atualizar detalhes da assinatura.');
    returnToList();
  }
};

const handleAccept = async (conviteId) => {
  try {
    await axios.post(`${API_BASE_URL}/convites/${conviteId}/aceitar`, {}, authConfig());
    showNotification('success', "Convite aceito com sucesso!");
    fetchAssinaturas(); 
  } catch (error) {
    showNotification('error', error.response?.data?.message || "Erro ao aceitar convite.");
  }
};

const handleDecline = async (conviteId) => {
  try {
    await axios.post(`${API_BASE_URL}/convites/${conviteId}/recusar`, {}, authConfig());
    showNotification('success', "Convite recusado.");
    convitesPendentes.value = convitesPendentes.value.filter(c => c.conviteId !== conviteId);
  } catch (error) {
    showNotification('error', error.response?.data?.message || "Erro ao recusar convite.");
  }
};

const handleCreate = async () => { 
  isSubmitting.value = true;
  try {
    const payload = { ...createForm.value, emailsMembros: (createForm.value.emailsMembros || '').split(/[\s,]+/).filter(Boolean) };
    const response = await axios.post(`${API_BASE_URL}/assinaturas-compartilhadas`, payload, authConfig());
    showCreateModal.value = false;
    createForm.value = { nomeStreamer: '', plataforma: '', valorMensal: null, diaVencimento: null, emailsMembros: '' };
    await fetchAssinaturas();
    if (response.data?.uuid) {
      await selectAssinatura(response.data.uuid); 
    }
    showNotification('success', 'Grupo de assinatura criado com sucesso!');
  } catch (error) {
    showNotification('error', error.response?.data?.message || 'Erro ao criar grupo.');
  } finally {
    isSubmitting.value = false;
  }
};

const openEditModal = () => { 
  if (!selectedAssinatura.value) return;
  editForm.value = {
    nomeStreamer: selectedAssinatura.value.nomeStreamer ?? '',
    plataforma: selectedAssinatura.value.plataforma ?? '',
    valorMensal: selectedAssinatura.value.valorMensal ?? 0,
    diaVencimento: selectedAssinatura.value.diaVencimento ?? 1,
  };
  showEditModal.value = true;
};

const handleUpdate = async () => { 
  if (!selectedAssinatura.value?.uuid) return;
  isSubmitting.value = true;
  try {
    await axios.put(`${API_BASE_URL}/assinaturas-compartilhadas/${selectedAssinatura.value.uuid}`, editForm.value, authConfig());
    await refreshSelectedAssinatura();
    showEditModal.value = false;
    showNotification('success', 'Assinatura atualizada com sucesso!');
  } catch (error) {
    showNotification('error', error.response?.data?.message || 'Erro ao atualizar assinatura.');
  } finally {
    isSubmitting.value = false;
  }
};

const handleInvite = async () => {
  if (!selectedAssinatura.value?.uuid) return;
  isSubmitting.value = true;
  try {
    await axios.post(`${API_BASE_URL}/assinaturas-compartilhadas/${selectedAssinatura.value.uuid}/convidar`, inviteForm.value, authConfig());
    await refreshSelectedAssinatura();
    showInviteModal.value = false;
    inviteForm.value.email = '';
    showNotification('success', 'Membro convidado com sucesso!');
  } catch (error) {
    showNotification('error', error.response?.data?.message || '"Não foi possível convidar o membro, ele precisa ser ativo no sistema."');
  } finally {
    isSubmitting.value = false;
  }
};

const confirmRemoveMember = (membro) => { memberToRemove.value = membro; showConfirmRemoveMember.value = true; };
const executeRemoveMember = async () => {
  if (!memberToRemove.value || !selectedAssinatura.value?.uuid) return;
  showConfirmRemoveMember.value = false;
  try {
    await axios.delete(`${API_BASE_URL}/assinaturas-compartilhadas/${selectedAssinatura.value.uuid}/membros/${memberToRemove.value.id}`, authConfig());
    await refreshSelectedAssinatura();
    showNotification('success', 'Membro removido com sucesso.');
  } catch (error) { showNotification('error', error.response?.data?.message || 'Erro ao remover membro.'); }
  finally { memberToRemove.value = null; }
};

const confirmCancelInvite = (membro) => { inviteToCancel.value = membro; showConfirmCancelInvite.value = true; };
const executeCancelInvite = async () => {
  if (!inviteToCancel.value?.conviteId) return; 
  showConfirmCancelInvite.value = false;
  try {
    await axios.delete(`${API_BASE_URL}/convites/${inviteToCancel.value.conviteId}/cancelar`, authConfig());
    await refreshSelectedAssinatura();
    showNotification('success', 'Convite cancelado com sucesso.');
  } catch (error) { showNotification('error', error.response?.data?.message || "Erro ao cancelar o convite."); }
  finally { inviteToCancel.value = null; }
};

const confirmLeaveGroup = () => { showConfirmLeaveGroup.value = true; };
const executeLeaveGroup = async () => {
  if (!selectedAssinatura.value?.uuid) return;
  showConfirmLeaveGroup.value = false;
  try {
    await axios.delete(`${API_BASE_URL}/assinaturas-compartilhadas/${selectedAssinatura.value.uuid}/membros/leave`, authConfig());
    showNotification('success', 'Você saiu do grupo.');
    await returnToList();
  } catch (error) { showNotification('error', error.response?.data?.message || 'Erro ao sair do grupo.'); }
};

const confirmEndGroup = () => { showConfirmEndGroup.value = true; };
const executeEndGroup = async () => {
  if (!selectedAssinatura.value?.uuid) return;
  showConfirmEndGroup.value = false;
  try {
    await axios.delete(`${API_BASE_URL}/assinaturas-compartilhadas/${selectedAssinatura.value.uuid}`, authConfig());
    showNotification('success', 'Grupo encerrado com sucesso.');
    await returnToList();
  } catch (error) { showNotification('error', error.response?.data?.message || 'Erro ao encerrar grupo.'); }
};

const openAddPagamentoModal = () => {
  if (!selectedAssinatura.value) return;
  pagamentoForm.value.valor = selectedAssinatura.value.valorPorPessoa ?? 0;
  showAddPagamentoModal.value = true;
};
const handleAddPagamento = async () => {
  if (!selectedAssinatura.value?.uuid) return;
  isSubmitting.value = true;
  try {
    await axios.post(`${API_BASE_URL}/assinaturas-compartilhadas/${selectedAssinatura.value.uuid}/pagamentos`, pagamentoForm.value, authConfig());
    await refreshSelectedAssinatura();
    showAddPagamentoModal.value = false;
    pagamentoForm.value = { valor: null };
    showNotification('success', 'Pagamento registrado com sucesso!');
  } catch (error) { showNotification('error', error.response?.data?.message || 'Erro ao registrar pagamento.'); }
  finally { isSubmitting.value = false; }
};

const openEditPagamentoModal = (pagamento) => {
  pagamentoToEdit.value = pagamento;
  editPagamentoForm.value.valor = pagamento?.valor ?? 0;
  showEditPagamentoModal.value = true;
};
const handleUpdatePagamento = async () => {
  if (!pagamentoToEdit.value || !selectedAssinatura.value?.uuid) return;
  isSubmitting.value = true;
  try {
    await axios.put(`${API_BASE_URL}/assinaturas-compartilhadas/${selectedAssinatura.value.uuid}/pagamentos/${pagamentoToEdit.value.id}`, editPagamentoForm.value, authConfig());
    await refreshSelectedAssinatura();
    showEditPagamentoModal.value = false;
    pagamentoToEdit.value = null;
    showNotification('success', 'Pagamento atualizado com sucesso!');
  } catch (error) { showNotification('error', error.response?.data?.message || 'Erro ao atualizar pagamento.'); }
  finally { isSubmitting.value = false; }
};

const confirmDeletePagamento = (pagamento) => { pagamentoToDelete.value = pagamento; showConfirmDeletePagamento.value = true; };
const executeDeletePagamento = async () => {
  if (!pagamentoToDelete.value || !selectedAssinatura.value?.uuid) return;
  showConfirmDeletePagamento.value = false;
  try {
    await axios.delete(`${API_BASE_URL}/assinaturas-compartilhadas/${selectedAssinatura.value.uuid}/pagamentos/${pagamentoToDelete.value.id}`, authConfig());
    await refreshSelectedAssinatura();
    showNotification('success', 'Pagamento removido com sucesso.');
  } catch (error) { showNotification('error', error.response?.data?.message || 'Erro ao remover pagamento.'); }
  finally { pagamentoToDelete.value = null; }
};

onMounted(() => {
  fetchCurrentUser();
  const assinaturaId = route.params.id; 
  if (assinaturaId) {
    selectAssinatura(assinaturaId); 
  } else {
    fetchAssinaturas();
    currentView.value = 'list'; 
  }
  checkDevice();
  window.addEventListener('resize', checkDevice);
});

onUnmounted(() => {
  window.removeEventListener('resize', checkDevice);
  if (notificationTimer.value) clearTimeout(notificationTimer.value);
});
</script>

<style scoped>
.form-label {
  @apply block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1;
}

.form-input {
  @apply w-full px-3 py-2 text-sm bg-white dark:bg-slate-700/60 border border-solid border-slate-300 dark:border-slate-600 rounded-lg transition ease-in-out focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-opacity-70 focus:border-brand-500 text-slate-800 dark:text-slate-200;
}

.btn-primary {
  @apply px-5 py-2.5 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-lg transition-colors duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed;
}

.btn-secondary {
  @apply px-5 py-2.5 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 dark:text-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 rounded-lg transition-colors duration-150 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed;
}

.btn-danger {
  @apply px-5 py-2.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors duration-150 ease-in-out;
}

.btn-danger-outline {
  @apply px-5 py-2.5 text-sm font-medium text-red-600 bg-transparent border border-red-500 hover:bg-red-50 dark:text-red-400 dark:border-red-500 dark:hover:bg-red-900/20 rounded-lg transition-colors duration-150 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed;
}

.btn-action-icon {
  @apply p-1.5 rounded-full transition-colors duration-200;
}

.card-assinatura {
  @apply bg-white dark:bg-brand-800 rounded-lg shadow-lg flex flex-col overflow-hidden transition-all duration-300 hover:shadow-xl dark:hover:shadow-brand-700/30 cursor-pointer hover:-translate-y-1;
}

.empty-state {
  @apply text-center py-16 text-slate-500 dark:text-slate-400 bg-white dark:bg-brand-800 rounded-xl shadow-md flex flex-col items-center justify-center;
}

.skeleton-card {
  @apply bg-white dark:bg-brand-800 rounded-lg shadow-lg animate-pulse overflow-hidden;
}

.tag-owner {
  @apply ml-2 text-xs font-medium bg-purple-100 text-purple-700 dark:bg-brand-700 dark:text-brand-500 px-2 py-0.5 rounded-full;
}

.tag-pending {
  @apply text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300 px-2 py-0.5 rounded-full;
}

.tag-attention {
  @apply text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300 px-2 py-0.5 rounded-full;
}

.modal-backdrop {
  @apply fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 dark:bg-brand-900/70 backdrop-blur-sm p-4;
}

.modal-container {
  @apply bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-lg transform transition-all;
}

.modal-header {
  @apply px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center text-xl font-semibold text-slate-800 dark:text-slate-100;
}

.modal-close-btn {
  @apply p-1 text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors;
}

.modal-footer {
  @apply p-4 bg-slate-50 dark:bg-slate-700/50 flex justify-end space-x-3 rounded-b-xl;
}

.modal-title {
  @apply mt-2 text-xl font-semibold text-slate-800 dark:text-slate-100;
}

.modal-text {
  @apply mt-2 text-sm text-slate-600 dark:text-slate-400;
}

.timeline-container {
  @apply border-l-2 border-slate-200 dark:border-slate-700;
}

.timeline-item {
  @apply relative pl-8 py-4;
}

.timeline-point {
  @apply bg-green-500 absolute -left-[9px] top-[28px] w-4 h-4 rounded-full border-4 border-white dark:border-brand-800;
}

.timeline-content {
  @apply ml-4;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
  transform: scale(0.95) translateY(-20px);
}

.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

.view-fade-enter-active,
.view-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.view-fade-enter-from {
  opacity: 0;
  transform: translateX(-10px);
}

.view-fade-leave-to {
  opacity: 0;
  transform: translateX(10px);
}

.list-enter-active,
.list-leave-active {
  transition: all 0.4s ease;
}

.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

.notification-enter-active,
.notification-leave-active {
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.notification-enter-from,
.notification-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

.animate-pulse-fast {
  animation: pulse-fast 2.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse-fast {
  50% {
    opacity: .85;
  }
}
</style>